import { Box, Container } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { SEO, StructuredData } from "../../../components/seo";
import Button from "../../../components/ui/Button";
import { GameDetailsSkeleton } from "../../../components/ui/SkeletonLoader";
import Text from "../../../components/ui/Text";
import { getGameSchema, getGameSEO } from "../../../constants/seo";
import { useToast } from "../../../contexts/ToastContext";
import apiService from "../../../services/api";
import { ActionButtons, GameHeader, PlayerList } from "./parts";

const GameDetails = () => {
  const navigate = useNavigate();
  const { gameId } = useParams();
  const { showToast } = useToast();
  const location = useLocation();
  const from = location.state?.from;
  const currentUserId = location.state?.currentUserId;

  // State for API data
  const [gameDetails, setGameDetails] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for Add Player modal
  const [players, setPlayers] = useState([]);
  const [teamSummary, setTeamSummary] = useState({});
  const [maxPlayers, setMaxPlayers] = useState(0);

  // State for waiting list
  const [waitlist, setWaitlist] = useState([]);

  // Fetch game details and players from API
  const fetchGameDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await apiService.fetchGameById(gameId);

      if (response.success) {
        if (from === "/signin" || from === "/account-setup") {
          const isCurrentUserInGame = response.data.active_players.some(
            (playerId) => playerId === currentUserId,
          );
          if (!isCurrentUserInGame) {
            navigate("/review-booking", {
              state: {
                gameData: response.data,
              },
            });
            return;
          }
        }
        setGameDetails(response.data || null);
      } else {
        setError(`Failed to fetch game details: ${response.status}`);
      }
    } catch (error) {
      setError("Failed to fetch game details");
    } finally {
      setIsLoading(false);
    }
  };
  const fetchPlayers = async () => {
    try {
      const response = await apiService.fetchGamePlayers(gameId);
      if (response.success) {
        const sortedPlayers = (response.data || []).sort((a, b) => {
          if (a.join_date && b.join_date) {
            return new Date(a.join_date) - new Date(b.join_date);
          }
          return 0;
        });
        setPlayers(sortedPlayers);
        setTeamSummary(response.team_summary || {});
        setMaxPlayers(response.max_players || 0);
      } else {
        showToast("Failed to fetch players", "error");
      }
    } catch (error) {
      showToast("An error occurred while fetching players", "error");
    }
  };
  const fetchWaitlist = async () => {
    try {
      const response = await apiService.fetchGameWaitlist(gameId);
      if (response.success) {
        setWaitlist(response.data || []);
      }
    } catch (error) {
      // Waitlist is a secondary concern - don't block the page on failure
    }
  };

  useEffect(() => {
    fetchGameDetails();
    fetchPlayers();
    fetchWaitlist();
  }, [gameId]);

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 2, pb: 4 }}>
        <GameDetailsSkeleton />
      </Container>
    );
  }

  if (error || !gameDetails) {
    return (
      <Container maxWidth="md" sx={{ mt: 2, pb: 4 }}>
        <SEO
          title="Game Details - FindASide"
          description="View sports game details on FindASide"
          keywords="game details, sports game, book game"
        />
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Text variant="h6" sx={{ color: "error.main", mb: 2 }}>
            {error || "Game not found"}
          </Text>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </Box>
      </Container>
    );
  }

  const gameSEO = getGameSEO(gameDetails);
  const gameSchema = getGameSchema(gameDetails);

  return (
    <Box
      maxWidth="lg"
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: 0, md: 4 },
        alignItems: { xs: "stretch", md: "flex-start" },
      }}
    >
      <SEO
        title={gameSEO.title}
        description={gameSEO.description}
        keywords={gameSEO.keywords}
        ogImage={gameSEO.ogImage}
      />
      {gameSchema && (
        <StructuredData data={gameSchema} id="game-details-schema" />
      )}
      {/* Game Details and Player List */}
      <Box
        sx={{
          flex: { xs: "none", md: 1 },
          backgroundColor: { xs: "transparent", md: "white" },
          borderRadius: { xs: 0, md: "8px" },
          boxShadow: { xs: "none", md: "0 2px 8px rgba(0,0,0,0.1)" },
          overflow: "hidden",
          pb: { xs: 18, md: 0 },
        }}
      >
        {/* Game Header */}
        <GameHeader gameDetails={gameDetails} />
        {/* Player List Section */}
        <PlayerList
          gameDetails={gameDetails}
          players={players}
          waitlist={waitlist}
          teamSummary={teamSummary}
          maxPlayers={maxPlayers}
          fetchPlayers={fetchPlayers}
          fetchGameDetails={fetchGameDetails}
        />
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          position: { xs: "fixed", md: "static" },
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 2,
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          width: { xs: "auto", md: "200px", lg: "272px" },
          bottom: { xs: 0, md: "auto" },
          left: { xs: 0, md: "auto" },
          right: { xs: 0, md: "auto" },
          zIndex: 10,
        }}
      >
        <ActionButtons
          gameDetails={gameDetails}
          players={players}
          waitlist={waitlist}
          maxPlayers={maxPlayers}
          fetchGameDetails={fetchGameDetails}
          fetchPlayers={fetchPlayers}
          fetchWaitlist={fetchWaitlist}
        />
      </Box>
    </Box>
  );
};

export default GameDetails;
